/** Conta da calculadora do guia "quanto cobrar por hora". Puro: o teste importa daqui. */

export const WEEKS_PER_YEAR = 52;
const MONTHS_PER_YEAR = 12;

export interface HourlyRateInput {
  /** Quanto quer ganhar por mês, em R$. */
  income: number;
  /** Custos fixos por mês, em R$ (no MEI, o imposto fixo entra aqui). */
  costs: number;
  /** Imposto sobre o faturamento, em % (0 no MEI). */
  taxPercent: number;
  hoursPerWeek: number;
  /** Semanas paradas por ano (férias, feriados, doença). */
  weeksOff: number;
  /** % das horas trabalhadas que vira atendimento pago. */
  billablePercent: number;
}

/**
 * Horas vendáveis por mês = horas por semana × (52 − semanas paradas) ÷ 12 × %.
 * Ex.: 40 × 48 ÷ 12 × 0,7 = 112.
 */
export function billableHoursPerMonth(input: Pick<HourlyRateInput, 'hoursPerWeek' | 'weeksOff' | 'billablePercent'>): number | null {
  const { hoursPerWeek, weeksOff, billablePercent } = input;
  if (![hoursPerWeek, weeksOff, billablePercent].every(Number.isFinite)) return null;
  if (hoursPerWeek <= 0 || weeksOff < 0 || weeksOff >= WEEKS_PER_YEAR) return null;
  if (billablePercent <= 0 || billablePercent > 100) return null;
  return hoursPerWeek * ((WEEKS_PER_YEAR - weeksOff) / MONTHS_PER_YEAR) * (billablePercent / 100);
}

/**
 * Valor da hora = (renda + custos) ÷ (1 − imposto) ÷ horas vendáveis por mês.
 */
export function computeHourlyRate(input: HourlyRateInput): number | null {
  const { income, costs, taxPercent } = input;
  if (![income, costs, taxPercent].every(Number.isFinite)) return null;
  if (income < 0 || costs < 0 || taxPercent < 0 || taxPercent >= 100) return null;
  const hours = billableHoursPerMonth(input);
  if (!hours || income + costs <= 0) return null;
  return (income + costs) / (1 - taxPercent / 100) / hours;
}

/**
 * Valor em R$ digitado: "4.000", "4.000,50", "4000,5" ou "29.90". O ponto só é
 * milhar quando separa grupos de 3 dígitos ("4.000"); senão é decimal.
 */
export function parseMoney(value: string): number {
  const v = value.trim().replace(/^R\$\s*/i, '');
  if (!v) return NaN;
  if (v.includes(',')) return toNumber(v.replace(/\./g, '').replace(',', '.'));
  if (/^\d{1,3}(\.\d{3})+$/.test(v)) return toNumber(v.replace(/\./g, ''));
  return toNumber(v);
}

/** Porcentagem ou horas: aceita "6,5" e "6.5". Ponto aqui nunca é milhar. */
export function parseDecimal(value: string): number {
  const v = value.trim().replace(/%$/, '').trim();
  if (!v) return NaN;
  return toNumber(v.replace(',', '.'));
}

const toNumber = (v: string): number => (/^-?\d+(\.\d+)?$/.test(v) ? Number(v) : NaN);

/** Exemplo que a calculadora abre preenchido e que o texto do guia usa. */
export const HOURLY_RATE_EXAMPLE: HourlyRateInput = {
  income: 4000,
  costs: 800,
  taxPercent: 6,
  hoursPerWeek: 40,
  weeksOff: 4,
  billablePercent: 70,
};
